import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectActiveOrganizationId } from "../../redux/authSlice";
import {
  Bar,
  BarChart,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import history from "../../utils/history";
import { store } from "../../utils/store";
import { openErrorNotification, openSuccessNotification } from "../../helpers/methods";
import { clickToCall } from "../Team/components/ShowAll/ShowAllService";
import YhSelect from "../../ui/YhSelect";
import { YhDateRangePicker } from "../../ui/YhDateTimePicker";
import urls from "../../global/constants/UrlConstants";
import {
  dashboardWhatsApp,
  getDashboardV2,
  nudgeDashboardAgent,
  reviewDashboardCall,
  transferUserLeads,
} from "./DashboardService";
import { getCallingReport } from "../CallLogs/callingReportService";

function Glyph({ paths, size = 16, className = "" }: { paths: string[]; size?: number; className?: string }) {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {paths.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

const FiUsers = () => <Glyph paths={["M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2", "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8", "M22 21v-2a4 4 0 0 0-3-3.87", "M16 3.13a4 4 0 0 1 0 7.75"]} />;
const FiUserPlus = () => <Glyph paths={["M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2", "M9 7a4 4 0 1 0 0 8 4 4 0 0 0 0-8", "M19 8v6", "M22 11h-6"]} />;
const FiPhone = () => <Glyph paths={["M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.35 1.9.68 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.33 1.85.55 2.81.68A2 2 0 0 1 22 16.92z"]} />;
const FiPhoneCall = FiPhone;
const FiHome = () => <Glyph paths={["M3 9.5 12 3l9 6.5V21a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1V9.5z"]} />;
const FiCheck = () => <Glyph paths={["M20 6 9 17l-5-5"]} />;
const FiTrendingUp = ({ className = "" }: { className?: string }) => <Glyph className={className} paths={["M23 6l-9.5 9.5-5-5L1 18", "M17 6h6v6"]} />;
const FiActivity = ({ className = "" }: { className?: string }) => <Glyph className={className} paths={["M22 12h-4l-3 9L9 3l-3 9H2"]} />;
const FiInbox = () => <Glyph paths={["M22 12h-6l-2 3h-4l-2-3H2", "M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"]} />;
const FiClock = () => <Glyph paths={["M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z", "M12 6v6l4 2"]} />;
const FiAlertCircle = ({ className = "" }: { className?: string }) => <Glyph className={className} paths={["M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z", "M12 8v4", "M12 16h.01"]} />;
const FiTarget = () => <Glyph paths={["M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z", "M12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12z", "M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"]} />;
const FiBarChart2 = () => <Glyph paths={["M18 20V10", "M12 20V4", "M6 20v-6"]} />;
const FiCalendar = ({ className = "" }: { className?: string }) => <Glyph className={className} paths={["M19 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z", "M16 2v4", "M8 2v4", "M3 10h18"]} />;
const FiDownload = () => <Glyph paths={["M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4", "M7 10l5 5 5-5", "M12 15V3"]} />;
const FiPlus = () => <Glyph paths={["M12 5v14", "M5 12h14"]} />;
const FiMessageSquare = () => <Glyph paths={["M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"]} />;
const FiMinus = () => <Glyph paths={["M5 12h14"]} />;
const FiArrowUpRight = () => <Glyph paths={["M7 17 17 7", "M7 7h10v10"]} />;
const FiArrowDownRight = () => <Glyph paths={["M7 7l10 10", "M17 7v10H7"]} />;
const FiSearch = () => <Glyph paths={["M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z", "M21 21l-4.35-4.35"]} />;
const FiHeadphones = () => <Glyph paths={["M3 18v-6a9 9 0 0 1 18 0v6", "M21 19a2 2 0 0 1-2 2h-1v-6h3v4z", "M3 19a2 2 0 0 0 2 2h1v-6H3v4z"]} />;
const FiRefreshCw = () => <Glyph paths={["M23 4v6h-6", "M1 20v-6h6", "M3.51 9a9 9 0 0 1 14.85-3.36L23 10", "M20.49 15a9 9 0 0 1-14.85 3.36L1 14"]} />;

function isoDate(ddmmyyyy: string) {
  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(ddmmyyyy);
  if (!match) return "";
  return `${match[3]}-${match[2]}-${match[1]}`;
}

type Role = "admin" | "team_leader" | "agent";

const KPI_META: Record<string, { icon: React.ReactNode; tint: string; wash: string }> = {
  leads: { icon: <FiUsers />, tint: "#841C39", wash: "#F9F0F4" },
  new: { icon: <FiUserPlus />, tint: "#111827", wash: "#F3F4F6" },
  calls: { icon: <FiPhone />, tint: "#841C39", wash: "#F9F0F4" },
  connect: { icon: <FiPhoneCall />, tint: "#0F766E", wash: "#ECFDF5" },
  visits: { icon: <FiHome />, tint: "#1D4ED8", wash: "#EFF6FF" },
  closures: { icon: <FiCheck />, tint: "#841C39", wash: "#F9F0F4" },
};

function fmtDur(seconds?: number) {
  const s = Math.max(0, Number(seconds) || 0);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

function initials(name?: string) {
  const parts = String(name || "A").trim().split(/\s+/).slice(0, 2);
  return parts.map((part) => part[0]?.toUpperCase() || "").join("") || "A";
}

function Panel({
  title,
  hint,
  icon,
  action,
  children,
  className = "",
}: {
  title: string;
  hint?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`flex h-full flex-col overflow-hidden rounded-2xl border border-[#E8E4E6] bg-white shadow-[0_8px_28px_rgba(132,28,57,0.05)] ${className}`}>
      <div className="flex items-start justify-between gap-3 border-b border-[#F3EEF0] px-5 py-4">
        <div className="flex items-start gap-3">
          {icon && (
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#F9F0F4] text-[#841C39]">
              {icon}
            </span>
          )}
          <div>
            <h3 className="text-[15px] font-bold text-[#111827]">{title}</h3>
            {hint && <p className="mt-0.5 text-xs text-[#6B7280]">{hint}</p>}
          </div>
        </div>
        {action}
      </div>
      <div className="flex-1 p-5">{children}</div>
    </section>
  );
}

function Delta({ value, active }: { value?: number; active?: boolean }) {
  if (!active) return <span className="text-xs text-[#9CA3AF]">Current snapshot</span>;
  const n = Number(value) || 0;
  if (!n) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-[#6B7280]">
        <FiMinus /> Same as previous period
      </span>
    );
  }
  const up = n > 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${up ? "text-emerald-700" : "text-[#841C39]"}`}>
      {up ? <FiArrowUpRight /> : <FiArrowDownRight />}
      {up ? "+" : ""}
      {n}% vs previous
    </span>
  );
}

function Meter({ value, max, tone = "#841C39" }: { value: number; max: number; tone?: string }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : value > 0 ? 100 : 0;
  return (
    <div className="h-2 overflow-hidden rounded-full bg-[#F3F4F6]">
      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: tone }} />
    </div>
  );
}

function Badge({ children, tone }: { children: React.ReactNode; tone: "ok" | "wait" | "off" | "hot" | "info" }) {
  const map = {
    ok: "bg-emerald-50 text-emerald-700",
    wait: "bg-amber-50 text-amber-800",
    off: "bg-[#F3F4F6] text-[#4B5563]",
    hot: "bg-[#F9F0F4] text-[#841C39]",
    info: "bg-sky-50 text-sky-800",
  };
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${map[tone]}`}>{children}</span>;
}

function recordingTone(label?: string): "ok" | "wait" | "off" {
  if (label === "Available") return "ok";
  if (label === "Pending sync") return "wait";
  return "off";
}

function presenceDot(presence?: string) {
  if (presence === "On calls") return "bg-emerald-500";
  if (presence === "Idle") return "bg-amber-500";
  return "bg-[#D1D5DB]";
}

function ScrollArea({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`max-h-[300px] overflow-auto overscroll-contain pr-1 [scrollbar-width:thin] ${className}`}>
      {children}
    </div>
  );
}

function DashboardLoader({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="rounded-3xl border border-[#E8E4E6] bg-white px-6 py-14 text-center shadow-[0_10px_30px_rgba(132,28,57,0.06)]">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F9F0F4]">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-[#F3D5DE] border-t-[#841C39]" />
      </div>
      <p className="text-lg font-bold text-[#111827]">{title}</p>
      <p className="mt-1 text-sm text-[#6B7280]">{detail}</p>
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="h-24 animate-pulse rounded-2xl bg-[#F6F3F4]" />
        ))}
      </div>
    </div>
  );
}

const PulseDashboards = () => {
  const activeOrganizationId = useSelector(selectActiveOrganizationId) || "";
  const [period, setPeriod] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [range, setRange] = useState<{ from: string | null; to: string | null }>({
    from: null,
    to: null,
  });
  const [teamId, setTeamId] = useState("");
  const [agentIds, setAgentIds] = useState<string[]>([]);
  const [queueTab, setQueueTab] = useState<"overdue" | "dueToday" | "newAssigned">("overdue");
  const [query, setQuery] = useState("");
  const [skipped, setSkipped] = useState<string[]>([]);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [calling, setCalling] = useState<any>(null);

  const load = async (next?: {
    period?: string;
    teamId?: string;
    agentIds?: string[];
    from?: string;
    to?: string;
  }) => {
    setLoading(true);
    try {
      const activePeriod = next?.period ?? period;
      const activeFrom = next?.from ?? from;
      const activeTo = next?.to ?? to;
      const response = await getDashboardV2({
        period: activePeriod || undefined,
        from: activePeriod === "custom" ? activeFrom : undefined,
        to: activePeriod === "custom" ? activeTo : undefined,
        teamId: (next?.teamId ?? teamId) || undefined,
        agentIds: (next?.agentIds ?? agentIds).join(",") || undefined,
      });
      if (response?.success === 0) {
        openErrorNotification(response?.message || "Could not load the dashboard");
        return;
      }
      setData(response);
      if (!period && response?.period?.key) setPeriod(response.period.key);
      try {
        const callingRes = await getCallingReport({
          period: activePeriod || response?.period?.key || "today",
          from: activePeriod === "custom" ? activeFrom : undefined,
          to: activePeriod === "custom" ? activeTo : undefined,
          teamIds: (next?.teamId ?? teamId) ? [next?.teamId ?? teamId] : [],
          agentIds: next?.agentIds ?? agentIds,
        });
        if (callingRes?.success !== 0) setCalling(callingRes);
      } catch {
        setCalling(null);
      }
    } catch (error: any) {
      openErrorNotification(error?.message || "Could not load the dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrganizationId]);

  const role: Role = data?.role || "agent";
  const kpis = data?.kpis || [];
  const agents = data?.agents || [];
  const needle = query.trim().toLowerCase();
  const match = (row: any) => {
    if (!needle) return true;
    return `${row.leadName || ""} ${row.name || ""} ${row.phoneMasked || ""} ${row.stage || ""} ${row.source || ""}`
      .toLowerCase()
      .includes(needle);
  };

  const queue = (data?.queue?.[queueTab] || []).filter((row: any) => !skipped.includes(String(row.leadId || row.id)) && match(row));
  const title = role === "admin" ? "Business dashboard" : role === "team_leader" ? "Team dashboard" : "My work";
  const periodKey = period || data?.period?.key || "today";

  const teamOptions = useMemo(
    () =>
      (data?.filters?.teams || []).map((team: any) => ({
        label: team.department ? `${team.name} · ${team.department}` : team.name,
        value: team.id,
      })),
    [data]
  );
  const agentOptions = useMemo(
    () =>
      (data?.filters?.agents || [])
        .filter((agent: any) => !teamId || (agent.teamIds || []).includes(teamId) || !agent.teamIds)
        .map((agent: any) => ({ label: agent.name, value: agent.id })),
    [data, teamId]
  );

  const callLead = async (leadId?: string, name?: string) => {
    if (!leadId) return;
    try {
      const uuid = store.getState().auth?.uuid;
      const response = await clickToCall({ leadId, leadName: name || "", leadPhone: "", uuid });
      if (response?.success === 0 || response?.success === false) {
        openErrorNotification(response?.message || "Unable to place call");
        return;
      }
      openSuccessNotification(response?.message || "Call initiated");
    } catch (error: any) {
      openErrorNotification(error?.message || "Unable to place call");
    }
  };

  const openWhatsApp = async (leadId?: string) => {
    if (!leadId) return;
    try {
      const response = await dashboardWhatsApp(leadId);
      if (response?.url) window.open(response.url, "_blank", "noopener,noreferrer");
      else openErrorNotification(response?.message || "WhatsApp is unavailable");
    } catch (error: any) {
      openErrorNotification(error?.message || "WhatsApp is unavailable");
    }
  };

  const callReportUrl = (extra?: { agentId?: string; callId?: string }) => {
    const params = new URLSearchParams();
    params.set("dashboard", "1");
    params.set("period", data?.period?.key || periodKey);
    if ((data?.period?.key || periodKey) === "custom") {
      if (from) params.set("from", from);
      if (to) params.set("to", to);
    }
    if (teamId) params.set("teamId", teamId);
    const ids = extra?.agentId ? [extra.agentId] : agentIds;
    if (ids.length) params.set("agentIds", ids.join(","));
    if (extra?.callId) params.set("focus", extra.callId);
    return `${urls.callLogs}?${params.toString()}`;
  };

  const exportAgents = () => {
    const header = ["Agent", "Presence", "Calls", "Connect %", "Talk time", "Follow-ups done", "Follow-ups due", "Missed %", "Open leads", "Idle leads"];
    const lines = agents.map((row: any) =>
      [row.name, row.presence, row.calls, row.connectRate, fmtDur(row.talkSeconds), row.followUpsDone, row.followUpsDue, row.missedRate, row.openLeads, row.idleLeads]
        .map((cell: any) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );
    const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "pulse-dashboard.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const attention = [
    { label: "Untouched 24h+", value: data?.needsAttention?.untouched || 0, icon: <FiInbox />, note: "Still Initial/Lead for 24h+" },
    { label: "Overdue follow-ups", value: data?.needsAttention?.overdueFollowUps || 0, icon: <FiClock />, note: "Due time has passed" },
    { label: "Missed callbacks", value: data?.needsAttention?.missedCallbacks || 0, icon: <FiPhone />, note: "No log within 2 hours" },
    { label: "SLA breaches", value: data?.needsAttention?.slaBreaches || 0, icon: <FiAlertCircle />, note: "Not called in 15 min" },
    ...(role === "team_leader"
      ? [
          { label: "Unassigned", value: data?.needsAttention?.unassigned || 0, icon: <FiUsers />, note: "No owner yet" },
          { label: "Idle agents", value: data?.needsAttention?.idleAgents || 0, icon: <FiActivity />, note: "No call for 30 min" },
        ]
      : []),
  ];

  const funnel = (role === "agent" ? data?.pipeline?.stages : data?.funnel) || [];
  const funnelMax = Math.max(1, ...funnel.map((row: any) => Number(row.count) || 0));
  const agingMax = Math.max(1, ...(data?.aging || []).map((row: any) => Number(row.count) || 0));
  const goals = data?.targets?.goals || {};
  const targetRows = [
    { label: "Calls", value: data?.targets?.calls || 0, goal: goals.calls || 0, icon: <FiPhone /> },
    { label: "Connected", value: data?.targets?.connected || 0, goal: goals.connected || 0, icon: <FiPhoneCall /> },
    { label: "Follow-ups", value: data?.targets?.followUps || 0, goal: goals.followUps || 0, icon: <FiClock /> },
    { label: "Site visits", value: data?.targets?.visits || 0, goal: goals.visits || 0, icon: <FiHome /> },
    { label: "Bookings", value: data?.targets?.bookings || 0, goal: goals.bookings || 0, icon: <FiTarget /> },
  ];

  if (!data) {
    return (
      <div className="min-h-full bg-[#F7F4F5] px-4 py-5 md:px-6">
        <DashboardLoader
          title="Loading dashboard"
          detail="Preparing your role, scope, and numbers"
        />
      </div>
    );
  }

  return (
    <div className="relative min-h-full bg-[#F7F4F5] px-4 py-5 md:px-6">
      {loading && (
        <div className="absolute inset-0 z-40 bg-[#F7F4F5]/80 px-4 py-5 backdrop-blur-[1px] md:px-6">
          <DashboardLoader
            title="Updating dashboard"
            detail="Applying the selected date, team, and member filters"
          />
        </div>
      )}
      <div className="relative z-20 mb-5 rounded-3xl border border-[#E8E4E6] bg-white shadow-[0_10px_30px_rgba(132,28,57,0.06)]">
        <div className="flex flex-col gap-4 overflow-hidden rounded-t-3xl bg-[linear-gradient(120deg,#841C39_0%,#5C1228_55%,#1A1A1A_100%)] px-5 py-5 text-white lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide">
              <FiBarChart2 />
              {role === "admin" ? "Super admin" : role === "team_leader" ? "Team leader" : "Agent"}
            </div>
            <h1 className="text-2xl font-bold">{title}</h1>
            <p className="mt-1 text-sm text-white/80">{data.scopeLabel} · {data.period?.label}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {role === "admin" && (
              <button type="button" onClick={exportAgents} className="inline-flex h-10 items-center gap-2 rounded-xl bg-white px-3 text-sm font-semibold text-[#841C39]">
                <FiDownload /> Export
              </button>
            )}
            {role === "agent" && (
              <button type="button" onClick={() => history.push("/all-leads/add")} className="inline-flex h-10 items-center gap-2 rounded-xl bg-white px-3 text-sm font-semibold text-[#841C39]">
                <FiPlus /> Add lead
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-3 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="inline-flex flex-wrap rounded-xl bg-[#F6F3F4] p-1">
            {[
              ["today", "Today"],
              ["week", "This week"],
              ["month", "This month"],
              ["year", "This year"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setPeriod(key);
                  setRange({ from: null, to: null });
                  setFrom("");
                  setTo("");
                  load({ period: key });
                }}
                className={`inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-semibold ${
                  periodKey === key ? "bg-white text-[#841C39] shadow-sm" : "text-[#4B5563]"
                }`}
              >
                <FiCalendar className="text-[14px]" />
                {label}
              </button>
            ))}
          </div>
          <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2">
            <YhDateRangePicker
              className={periodKey === "custom" ? "[&>button]:border-[#841C39] [&>button]:bg-[#F9F0F4] [&>button]:text-[#841C39]" : ""}
              placeholder="Custom range"
              value={range}
              onChange={(next) => {
                setRange(next);
                if (!next.from || !next.to) return;
                const start = isoDate(next.from);
                const end = isoDate(next.to);
                if (!start || !end) return;
                setFrom(start);
                setTo(end);
                setPeriod("custom");
                load({ period: "custom", from: start, to: end });
              }}
            />
            {role === "admin" && (
              <div className="w-[210px]">
                <YhSelect
                  searchable
                  clearable
                  searchPlaceholder="Search teams"
                  placeholder="All teams"
                  value={teamId}
                  options={teamOptions}
                  onChange={(event) => {
                    const next = String(event.target.value || "");
                    setTeamId(next);
                    setAgentIds([]);
                    load({ teamId: next, agentIds: [] });
                  }}
                />
              </div>
            )}
            {role !== "agent" && (
              <div className="w-[230px]">
                <YhSelect
                  searchable
                  clearable
                  searchPlaceholder="Search agents"
                  placeholder="All members"
                  value=""
                  options={agentOptions}
                  onChange={(event) => {
                    const id = String(event.target.value || "");
                    if (!id || agentIds.includes(id)) return;
                    const next = [...agentIds, id];
                    setAgentIds(next);
                    load({ agentIds: next });
                  }}
                />
              </div>
            )}
            <label className="flex h-10 min-w-[200px] items-center gap-2 rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm text-[#6B7280]">
              <FiSearch />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search leads"
                className="w-full bg-transparent text-[#111827] outline-none"
              />
            </label>
          </div>
        </div>
        {data?.selectedTeam && (
          <p className="px-5 pb-4 text-sm text-[#6B7280]">
            Cards, funnel, and calls are limited to {data.selectedTeam.name}
            {data.selectedTeam.department ? ` · ${data.selectedTeam.department}` : ""} · {data.selectedTeam.members} members
          </p>
        )}
        {agentIds.length > 0 && (
          <div className="flex flex-wrap gap-2 px-5 pb-4">
            {agentIds.map((id) => {
              const agent = (data?.filters?.agents || []).find((row: any) => row.id === id);
              return (
                <button
                  key={id}
                  type="button"
                  className="inline-flex items-center gap-2 rounded-full bg-[#F9F0F4] px-3 py-1 text-xs font-semibold text-[#841C39]"
                  onClick={() => {
                    const next = agentIds.filter((item) => item !== id);
                    setAgentIds(next);
                    load({ agentIds: next });
                  }}
                >
                  {agent?.name || "Agent"} ×
                </button>
              );
            })}
          </div>
        )}
      </div>

      {data?.empty ? (
        <Panel title="No leads assigned yet" hint="Your queue starts as soon as a lead is assigned to you" icon={<FiInbox />}>
          <div className="grid gap-3 md:grid-cols-3">
            {[
              ["1", "Get assigned", "Ask your team leader to assign the first lead."],
              ["2", "Add a lead", "Create one yourself if your role allows it."],
              ["3", "Your numbers only", "Calls, stages, and follow-ups stay limited to you."],
            ].map(([step, label, copy]) => (
              <div key={step} className="rounded-2xl border border-[#F3EEF0] bg-[#FCFAFB] p-4">
                <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-full bg-[#841C39] text-sm font-bold text-white">{step}</div>
                <p className="font-semibold text-[#111827]">{label}</p>
                <p className="mt-1 text-sm text-[#6B7280]">{copy}</p>
              </div>
            ))}
          </div>
        </Panel>
      ) : (
        <div className="space-y-4">
          {role !== "agent" && (
            <div className={`grid gap-3 ${role === "team_leader" ? "md:grid-cols-3 xl:grid-cols-6" : "md:grid-cols-2 xl:grid-cols-4"}`}>
              {attention.map((item) => (
                <div key={item.label} className="rounded-2xl border border-[#E8E4E6] bg-white p-4 shadow-[0_8px_24px_rgba(132,28,57,0.04)]">
                  <div className="flex items-center justify-between">
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#F9F0F4] text-[#841C39]">{item.icon}</span>
                    <span className="text-2xl font-bold text-[#111827]">{item.value}</span>
                  </div>
                  <p className="mt-3 text-sm font-semibold text-[#111827]">{item.label}</p>
                  <p className="text-xs text-[#6B7280]">{item.note} · always now</p>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 xl:grid-cols-6">
            {kpis.map((kpi: any) => {
              const meta = KPI_META[kpi.key] || KPI_META.leads;
              return (
                <div key={kpi.key} className="rounded-2xl border border-[#E8E4E6] bg-white p-4">
                  <div className="flex items-center justify-between">
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl text-lg" style={{ background: meta.wash, color: meta.tint }}>
                      {meta.icon}
                    </span>
                    {kpi.followsDate ? <FiTrendingUp className="text-[#841C39]" /> : <FiActivity className="text-[#9CA3AF]" />}
                  </div>
                  <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">{kpi.label}</p>
                  <p className="mt-1 text-[28px] font-bold leading-none text-[#111827]">
                    {kpi.value ?? 0}{kpi.suffix || ""}
                  </p>
                  <div className="mt-2">
                    <Delta value={kpi.deltaPct} active={kpi.followsDate && Boolean(kpi.value || kpi.deltaPct)} />
                  </div>
                </div>
              );
            })}
          </div>

          {(role === "agent" || role === "team_leader") && (
            <Panel title={role === "agent" ? "My targets" : "Team targets"} hint="Daily targets added across the selected dates" icon={<FiTarget />}>
              <div className="grid gap-4 md:grid-cols-5">
                {targetRows.map((row) => (
                  <div key={row.label}>
                    <div className="mb-2 flex items-center justify-between text-sm">
                      <span className="inline-flex items-center gap-2 font-semibold text-[#111827]">{row.icon}{row.label}</span>
                      <span className="text-[#6B7280]">{row.value}/{row.goal || "—"}</span>
                    </div>
                    <Meter value={row.value} max={row.goal} />
                  </div>
                ))}
              </div>
            </Panel>
          )}

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
            {role === "agent" && (
              <div className="xl:col-span-5">
                <Panel title="Call next" hint="Most urgent lead right now" icon={<FiPhoneCall />}>
                  {data?.callNext ? (
                    <div className="rounded-2xl bg-[linear-gradient(160deg,#F9F0F4,#FFFFFF)] p-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#841C39] text-sm font-bold text-white">
                          {initials(data.callNext.name)}
                        </div>
                        <div>
                          <p className="text-lg font-bold text-[#111827]">{data.callNext.name}</p>
                          <p className="text-sm text-[#6B7280]">{data.callNext.phoneMasked}</p>
                        </div>
                      </div>
                      <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                        <div className="rounded-xl bg-white px-3 py-2"><span className="block text-xs text-[#6B7280]">Stage</span>{data.callNext.stage || "—"}</div>
                        <div className="rounded-xl bg-white px-3 py-2"><span className="block text-xs text-[#6B7280]">Last contact</span>{data.callNext.lastContact || "—"}</div>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button type="button" onClick={() => callLead(data.callNext.id, data.callNext.name)} className="inline-flex items-center gap-2 rounded-xl bg-[#841C39] px-3 py-2 text-sm font-semibold text-white"><FiPhone /> Call now</button>
                        <button type="button" onClick={() => openWhatsApp(data.callNext.id)} className="inline-flex items-center gap-2 rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-sm font-semibold"><FiMessageSquare /> WhatsApp</button>
                        <button type="button" onClick={() => setSkipped((ids) => [...ids, String(data.callNext.id)])} className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-sm font-semibold">Skip</button>
                        <button type="button" onClick={() => history.push(`/all-leads-details/${data.callNext.id}`)} className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-sm font-semibold">Log outcome</button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-[#6B7280]">No lead is waiting.</p>
                  )}
                </Panel>
              </div>
            )}

            <div className={role === "agent" ? "xl:col-span-7" : "xl:col-span-7"}>
              <Panel
                title={role === "agent" ? "Today’s call queue" : "Pre-sales funnel"}
                hint={role === "agent" ? "Overdue first, then due today, then new" : "Largest stages in the graph · every stage in the breakdown"}
                icon={role === "agent" ? <FiClock /> : <FiBarChart2 />}
              >
                {role === "agent" ? (
                  <>
                    <div className="mb-4 flex gap-2">
                      {([
                        ["overdue", "Overdue"],
                        ["dueToday", "Due today"],
                        ["newAssigned", "New assigned"],
                      ] as const).map(([key, label]) => (
                        <button key={key} type="button" onClick={() => setQueueTab(key)} className={`rounded-full px-3 py-1.5 text-sm font-semibold ${queueTab === key ? "bg-[#841C39] text-white" : "bg-[#F6F3F4] text-[#4B5563]"}`}>
                          {label}
                        </button>
                      ))}
                    </div>
                    <ScrollArea>
                      <LeadTable rows={queue} onCall={callLead} onWhatsApp={openWhatsApp} />
                    </ScrollArea>
                  </>
                ) : (
                  <>
                    {data?.stageSkip?.warning && (
                      <div className="mb-4 flex items-start gap-2 rounded-xl bg-[#F9F0F4] px-3 py-2 text-sm text-[#841C39]">
                        <FiAlertCircle className="mt-0.5 shrink-0" />
                        {data.stageSkip.closures} closures against {data.stageSkip.opportunities} opportunities. Opportunity should be set before Closure.
                      </div>
                    )}
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <div>
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">Graph</p>
                        <div className="h-[260px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={[...funnel].sort((a: any, b: any) => b.count - a.count).slice(0, 8)} layout="vertical" margin={{ left: 4, right: 28, top: 4, bottom: 4 }}>
                              <XAxis type="number" hide />
                              <YAxis type="category" dataKey="stage" width={120} tick={{ fontSize: 11, fill: "#4B5563" }} />
                              <Tooltip />
                              <Bar dataKey="count" fill="#841C39" radius={[0, 8, 8, 0]} barSize={14}>
                                <LabelList dataKey="count" position="right" style={{ fontSize: 11, fill: "#111827" }} />
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                      <div>
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">Breakdown</p>
                        <ScrollArea>
                          <div className="space-y-2.5">
                            {funnel.map((row: any) => (
                              <div key={row.stage}>
                                <div className="mb-1 flex justify-between gap-3 text-xs text-[#4B5563]">
                                  <span className="truncate font-semibold text-[#111827]">{row.stage}</span>
                                  <span className="shrink-0">{row.count} · {row.conversionPct || 0}%</span>
                                </div>
                                <Meter value={row.count} max={funnelMax} />
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>
                    </div>
                  </>
                )}
              </Panel>
            </div>

            {role !== "agent" && (
              <div className="xl:col-span-5">
                <Panel title="Open lead aging" hint="Days since last activity · current state" icon={<FiClock />}>
                  <div className="space-y-4">
                    {(data?.aging || []).map((row: any) => (
                      <div key={row.bucket}>
                        <div className="mb-1 flex justify-between text-sm">
                          <span className="font-semibold">{row.bucket} days</span>
                          <span>{row.count}</span>
                        </div>
                        <Meter value={row.count} max={agingMax} tone={row.bucket === "30+" ? "#111827" : "#841C39"} />
                      </div>
                    ))}
                  </div>
                </Panel>
              </div>
            )}

            {role === "agent" && (
              <div className="xl:col-span-12 grid grid-cols-1 gap-4 xl:grid-cols-2">
                <Panel title="My pipeline" hint={`${data?.pipeline?.idleLeads || 0} leads with no contact for 3+ days`} icon={<FiBarChart2 />}>
                  <ScrollArea>
                  <div className="space-y-3">
                    {funnel.map((row: any) => (
                      <div key={row.stage}>
                        <div className="mb-1 flex justify-between text-sm"><span>{row.stage || "Unset"}</span><b>{row.count}</b></div>
                        <Meter value={row.count} max={funnelMax} />
                      </div>
                    ))}
                  </div>
                  </ScrollArea>
                </Panel>
                <Panel title="Missed follow-ups" hint={`Rate ${data?.missed?.rate || 0}% · target under ${data?.missed?.target || 10}% · recovered ${data?.missed?.recovered || 0}`} icon={<FiAlertCircle />}>
                  <ScrollArea>
                    <LeadTable rows={(data?.missed?.latest || []).filter(match)} onCall={callLead} onWhatsApp={openWhatsApp} />
                  </ScrollArea>
                </Panel>
              </div>
            )}
          </div>

          {role !== "agent" && (
            <Panel title="Agent performance" hint={role === "team_leader" ? "Only agents on your team" : "Filtered by the team and agents above"} icon={<FiUsers />}>
              <div className="max-h-[420px] overflow-auto [scrollbar-width:thin]">
                <table className="w-full min-w-[860px] text-left text-sm">
                  <thead className="sticky top-0 z-10 bg-white">
                    <tr className="text-xs uppercase tracking-wide text-[#6B7280]">
                      <th className="pb-3 font-semibold">Agent</th>
                      <th className="pb-3 font-semibold">Calls</th>
                      <th className="pb-3 font-semibold">Connect</th>
                      <th className="pb-3 font-semibold">Talk</th>
                      <th className="pb-3 font-semibold">Follow-ups</th>
                      <th className="pb-3 font-semibold">Missed</th>
                      <th className="pb-3 font-semibold">Open / idle</th>
                      <th className="pb-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map((row: any) => (
                      <tr key={row.id} className="border-t border-[#F3EEF0]">
                        <td className="py-3">
                          <div className="flex items-center gap-3">
                            <div className="relative flex h-9 w-9 items-center justify-center rounded-full bg-[#F9F0F4] text-xs font-bold text-[#841C39]">
                              {initials(row.name)}
                              <span className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-white ${presenceDot(row.presence)}`} />
                            </div>
                            <div>
                              <div className="font-semibold text-[#111827]">{row.name}</div>
                              <div className="text-xs text-[#6B7280]">{row.presence} · {row.lastActivity}</div>
                            </div>
                          </div>
                        </td>
                        <td className="font-semibold">{row.calls}</td>
                        <td>
                          <div className="w-24">
                            <div className="mb-1 text-xs">{row.connectRate}%</div>
                            <Meter value={row.connectRate} max={100} tone="#0F766E" />
                          </div>
                        </td>
                        <td>{fmtDur(row.talkSeconds)}</td>
                        <td>{row.followUpsDone}/{row.followUpsDue}</td>
                        <td><Badge tone={row.missedRate > (data?.missed?.target || 10) ? "hot" : "ok"}>{row.missedRate}%</Badge></td>
                        <td>{row.openLeads} / {row.idleLeads}</td>
                        <td className="space-x-2 whitespace-nowrap">
                          {role === "team_leader" && (
                            <button type="button" className="rounded-lg bg-[#F9F0F4] px-2 py-1 text-xs font-semibold text-[#841C39]" onClick={async () => {
                              try {
                                await nudgeDashboardAgent(row.id);
                                openSuccessNotification(`Nudged ${row.name}`);
                              } catch (error: any) {
                                openErrorNotification(error?.message || "Could not nudge");
                              }
                            }}>Nudge</button>
                          )}
                          <button type="button" className="rounded-lg border border-[#E5E7EB] px-2 py-1 text-xs font-semibold" onClick={() => history.push(callReportUrl({ agentId: row.id }))}>Listen</button>
                        </td>
                      </tr>
                    ))}
                    {!agents.length && (
                      <tr><td colSpan={8} className="py-6 text-sm text-[#6B7280]">No agents in this filter.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}

          <Panel
            title="Daily calling report"
            hint="Assigned leads, calls, connect % — export the full grid from Reports"
            icon={<FiBarChart2 />}
            action={
              <button
                type="button"
                className="rounded-lg bg-[#841C39] px-3 py-1.5 text-xs font-semibold text-white"
                onClick={() => history.push(urls.callingReport)}
              >
                Open report
              </button>
            }
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wide text-[#6B7280]">
                    <th className="pb-2 font-semibold">Salesperson</th>
                    <th className="pb-2 text-right font-semibold">Assigned</th>
                    <th className="pb-2 text-right font-semibold">Calls</th>
                    <th className="pb-2 text-right font-semibold">Connected</th>
                    <th className="pb-2 text-right font-semibold">Connect %</th>
                  </tr>
                </thead>
                <tbody>
                  {(calling?.summaryRows || calling?.rows || []).slice(0, 8).map((row: any) => (
                    <tr key={row.id} className="border-t border-[#F3EEF0]">
                      <td className="py-2 font-semibold text-[#111827]">{row.name}</td>
                      <td className="py-2 text-right tabular-nums">{row.assignedLeads || 0}</td>
                      <td className="py-2 text-right tabular-nums">{row.totalCalls || 0}</td>
                      <td className="py-2 text-right tabular-nums">{row.connected || 0}</td>
                      <td className="py-2 text-right tabular-nums">{row.connectPct || 0}%</td>
                    </tr>
                  ))}
                  {!(calling?.rows || []).length && (
                    <tr>
                      <td colSpan={5} className="py-4 text-sm text-[#6B7280]">
                        No calls in this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Panel>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <Panel title={role === "agent" ? "My calls" : "IVR calls"} hint="Talk time is separate from ring time" icon={<FiHeadphones />}>
              <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[
                  ["Total", data?.ivr?.total || 0],
                  ["Answered", data?.ivr?.answered || 0],
                  ["Busy", data?.ivr?.busy || 0],
                  ["No answer", data?.ivr?.noAnswer || 0],
                ].map(([label, value]) => (
                  <div key={String(label)} className="rounded-xl bg-[#FCFAFB] px-3 py-2">
                    <p className="text-xs text-[#6B7280]">{label}</p>
                    <p className="text-lg font-bold">{value}</p>
                  </div>
                ))}
              </div>
              <p className="mb-3 text-xs text-[#6B7280]">Avg talk {fmtDur(data?.ivr?.avgTalkSeconds)} · Recordings pending {data?.ivr?.recordingsPending || 0}</p>
              <ScrollArea>
                <CallTable rows={(data?.recentCalls || []).filter(match)} />
              </ScrollArea>
            </Panel>

            <Panel title={role === "agent" ? "My recent leads" : "Follow-ups due today"} hint={role === "agent" ? "Latest assigned leads" : "Overdue rows stay on top"} icon={<FiCalendar />}>
              <ScrollArea>
                <LeadTable
                  rows={(role === "agent" ? data?.recentLeads : data?.followUpsToday || []).filter(match)}
                  onCall={callLead}
                  onWhatsApp={openWhatsApp}
                />
              </ScrollArea>
            </Panel>

            {role !== "agent" && (
              <Panel title="Lead source" hint="Cost per lead needs a monthly spend figure, which is not stored yet" icon={<FiTrendingUp />}>
                <ScrollArea>
                <div className="space-y-3">
                  {(data?.sources || []).length === 0 && <p className="text-sm text-[#6B7280]">No leads were created in this period.</p>}
                  {(data?.sources || []).map((row: any) => (
                    <div key={row.source}>
                      <div className="mb-1 flex items-center justify-between text-sm">
                        <span className="font-semibold">{row.source}</span>
                        <span className="text-[#6B7280]">{row.leads} leads · {row.closures} closures · {row.conversionPct}%</span>
                      </div>
                      <Meter value={row.conversionPct} max={100} />
                    </div>
                  ))}
                </div>
                </ScrollArea>
              </Panel>
            )}

            {role === "team_leader" && (
              <Panel title="Lead load" hint="Open leads and idle leads, with a rebalance when the gap is large" icon={<FiUsers />}>
                <ScrollArea>
                <div className="space-y-3">
                  {agents.map((row: any) => (
                    <div key={row.id}>
                      <div className="mb-1 flex justify-between text-sm">
                        <span className="font-semibold">{row.name}</span>
                        <span className="text-[#6B7280]">{row.openLeads} open · {row.idleLeads} idle</span>
                      </div>
                      <Meter value={row.openLeads} max={Math.max(1, ...agents.map((item: any) => item.openLeads || 0))} />
                    </div>
                  ))}
                </div>
                </ScrollArea>
                {data?.rebalance && (
                  <div className="mt-4 rounded-2xl bg-[#F9F0F4] p-4">
                    <p className="text-sm text-[#111827]">{data.rebalance.suggestion}</p>
                    <button
                      type="button"
                      className="mt-3 inline-flex items-center gap-2 rounded-xl bg-[#841C39] px-3 py-2 text-sm font-semibold text-white"
                      onClick={async () => {
                        const gap = Math.max(
                          1,
                          Math.ceil(
                            ((data.rebalance.fromOpenLeads || 0) -
                              (data.rebalance.toOpenLeads || 0)) /
                              2
                          )
                        );
                        const ok = window.confirm(
                          `Move ${gap} of the oldest open leads from ${data.rebalance.fromName} to ${data.rebalance.toName}?`
                        );
                        if (!ok) return;
                        try {
                          await transferUserLeads({
                            fromUserId: data.rebalance.fromUserId,
                            toUserId: data.rebalance.toUserId,
                            limit: gap,
                          });
                          openSuccessNotification(`Moved ${gap} leads`);
                          load();
                        } catch (error: any) {
                          openErrorNotification(error?.message || "Could not rebalance");
                        }
                      }}
                    >
                      <FiRefreshCw /> Move oldest leads
                    </button>
                  </div>
                )}
              </Panel>
            )}

            {role !== "agent" && (
              <Panel title="Calls to review" hint="Short calls, missed calls, and calls not yet marked reviewed" icon={<FiHeadphones />}>
                {(data?.callsToReview || []).length === 0 && <p className="text-sm text-[#6B7280]">Nothing is waiting for review.</p>}
                <ScrollArea>
                <div className="space-y-3">
                  {(data?.callsToReview || []).filter(match).map((call: any) => (
                    <div key={call.id} className="flex items-center justify-between gap-3 rounded-xl border border-[#F3EEF0] px-3 py-3">
                      <div>
                        <p className="font-semibold text-[#111827]">{call.leadName} · {call.phoneMasked}</p>
                        <p className="text-xs text-[#6B7280]">{call.time} · {call.direction} · {call.agentName || "Agent"} · Talk {fmtDur(call.talkTime)} · Ring {fmtDur(call.ringTime)}</p>
                        <div className="mt-1"><Badge tone={recordingTone(call.recording)}>{call.recording}</Badge></div>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <button type="button" className="rounded-lg border px-2 py-1 text-xs font-semibold" onClick={() => history.push(callReportUrl({ callId: call.id }))}>Play</button>
                        <button type="button" className="rounded-lg bg-[#841C39] px-2 py-1 text-xs font-semibold text-white" onClick={async () => {
                          try {
                            await reviewDashboardCall(call.id);
                            openSuccessNotification("Marked reviewed");
                            load();
                          } catch (error: any) {
                            openErrorNotification(error?.message || "Could not mark reviewed");
                          }
                        }}>Mark reviewed</button>
                      </div>
                    </div>
                  ))}
                </div>
                </ScrollArea>
              </Panel>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function LeadTable({
  rows,
  onCall,
  onWhatsApp,
}: {
  rows: any[];
  onCall: (id?: string, name?: string) => void;
  onWhatsApp: (id?: string) => void;
}) {
  if (!rows.length) return <p className="text-sm text-[#6B7280]">Nothing matches this list.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] text-left text-sm">
        <thead className="sticky top-0 z-10 bg-white">
          <tr className="text-xs uppercase tracking-wide text-[#6B7280]">
            <th className="pb-2 font-semibold">Lead</th>
            <th className="pb-2 font-semibold">Stage</th>
            <th className="pb-2 font-semibold">When</th>
            <th className="pb-2 font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id || row.leadId} className="border-t border-[#F3EEF0]">
              <td className="py-2.5">
                <button type="button" className="text-left" onClick={() => history.push(`/all-leads-details/${row.leadId || row.id}`)}>
                  <div className="font-semibold text-[#111827]">{row.leadName || row.name}</div>
                  <div className="text-xs text-[#6B7280]">{row.phoneMasked}</div>
                </button>
              </td>
              <td>{row.stage ? <Badge tone="info">{row.stage}</Badge> : row.overdue ? <Badge tone="hot">Overdue</Badge> : <span className="text-[#6B7280]">{row.reason || row.title || "—"}</span>}</td>
              <td className="text-xs text-[#6B7280]">{row.due || row.lastContact || row.nextFollowUp || "—"}</td>
              <td className="space-x-1 whitespace-nowrap">
                <button type="button" className="rounded-lg bg-[#841C39] px-2 py-1 text-xs font-semibold text-white" onClick={() => onCall(row.leadId || row.id, row.leadName || row.name)}>Call</button>
                <button type="button" className="rounded-lg border px-2 py-1 text-xs font-semibold" onClick={() => onWhatsApp(row.leadId || row.id)}>WA</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CallTable({ rows }: { rows: any[] }) {
  if (!rows.length) return <p className="text-sm text-[#6B7280]">No calls in this period.</p>;
  return (
    <div className="space-y-2">
      {rows.map((call) => (
        <div key={call.id} className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-xl border border-[#F3EEF0] px-3 py-2.5">
          <div>
            <p className="text-sm font-semibold text-[#111827]">{call.time} · {call.leadName}</p>
            <p className="text-xs text-[#6B7280]">{call.phoneMasked} · {call.direction} · {call.agentName || "—"} · Talk {fmtDur(call.talkTime)} · Ring {fmtDur(call.ringTime)}</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge tone={call.status === "Answered" ? "ok" : call.status === "Busy" ? "wait" : "off"}>{call.status}</Badge>
            <Badge tone={recordingTone(call.recording)}>{call.recording}</Badge>
          </div>
        </div>
      ))}
    </div>
  );
}

export default PulseDashboards;
